import { useMemo, useState } from 'react'

import {
  useImportPersonaCatalogApi,
  usePersonaCatalogApi,
  usePersonaCatalogDetailApi,
  usePersonaReadinessApi,
  useUpdatePersonaCatalogApi
} from '@/common/api/modules/persona'
import {
  ImportPersonaCatalogResponse,
  PersonaCatalogItem,
  PersonaConditionLeaf,
  PersonaDifficultyBand,
  PersonaMetricBrief,
  PersonaMetricDef
} from '@/common/api/params/persona'

import RateBar from '@/components/view/atoms/bar/RateBar'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import ConditionLeafEditor from '@/components/container/modules/persona/ConditionLeafEditor'
import DefinitionHistory from '@/components/container/modules/persona/DefinitionHistory'
import {
  hasEmptyValue,
  toEditableLeaves
} from '@/components/container/modules/persona/conditionLeaves'

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

const BANDS: { key: PersonaDifficultyBand | 'all'; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: '1-3', label: '難易度1-3' },
  { key: '4-6', label: '難易度4-6' },
  { key: '7-8', label: '難易度7-8' },
  { key: '9-10', label: '難易度9-10' }
]

/** 開発規模のラベル色。大きいものほど目立たせる */
const EFFORT_COLOR: Record<string, TextColor> = {
  小: TextColor.SUCCESS,
  中: TextColor.CAUTION,
  大: TextColor.CRITICAL
}

/** 難易度帯ごとの細い進捗バー（RateBar は数値が大きく行に収まらないため別に用意） */
const MiniBar = ({ percent }: { percent: number }) => (
  <div className='bg-fill-tertiary h-2 w-full overflow-hidden rounded-full'>
    <div className='bg-fill-primary-brand h-full rounded-full' style={{ width: `${percent}%` }} />
  </div>
)

/** 判定可能性と、何を作れば何体増えるか */
const Readiness = () => {
  const { data, isLoading } = usePersonaReadinessApi()
  if (isLoading || !data) {
    return (
      <Area>
        <AreaContent>
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        </AreaContent>
      </Area>
    )
  }

  const rate = data.total > 0 ? Math.round((data.judgeable / data.total) * 100) : 0
  return (
    <>
      <Area>
        <Heading subText='カタログのうち、判定条件を式にできている体数。残りは「該当者が0人」ではなく「まだ判定できない」状態を表す。'>
          判定できる体数
        </Heading>
        <AreaContent>
          <div className='flex flex-wrap items-center gap-x-8 gap-y-3'>
            <div>
              <Text size={12} color={TextColor.SECONDARY}>
                カタログ全体
              </Text>
              <div>
                <Text size={24} weight='bold'>
                  {data.judgeable} / {data.total} 体
                </Text>
              </div>
            </div>
            <div className='min-w-[240px] flex-1'>
              <RateBar percent={rate} />
            </div>
          </div>

          <div className='mt-5'>
            <Text size={12} color={TextColor.SECONDARY}>
              難易度帯ごとの内訳
            </Text>
            <div className='mt-2 flex flex-col gap-2'>
              {data.by_difficulty.map((b) => {
                const p = b.total > 0 ? Math.round((b.judgeable / b.total) * 100) : 0
                return (
                  <div key={b.band} className='flex items-center gap-3'>
                    <div className='w-24 shrink-0'>
                      <Text size={12}>難易度 {b.band}</Text>
                    </div>
                    <div className='w-20 shrink-0 text-right'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {b.judgeable} / {b.total} 体
                      </Text>
                    </div>
                    <div className='max-w-[420px] min-w-[160px] flex-1'>
                      <MiniBar percent={p} />
                    </div>
                    <div className='w-10 shrink-0 text-right'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {p}%
                      </Text>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </AreaContent>
      </Area>

      <Area>
        <Heading subText='「単独で」はその機能だけを作れば判定できるようになる体数、「関与」は他にも足りない機能がある体を含めた数。1体が複数の機能を必要とするため、両方を見ないと優先順位を誤る。'>
          次に何を作れば何体増えるか
        </Heading>
        <AreaContent>
          <Table>
            <thead>
              <TableRow head>
                <TableHeadItem>
                  <Text size={12}>作るもの</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>単独で</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>関与</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>規模</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>実装内容と対象ペルソナ</Text>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {data.capabilities.map((c) => (
                <TableRow key={c.key}>
                  <TableRowItem>
                    <Text size={14} weight='bold'>
                      {c.label}
                    </Text>
                    <div className='mt-1'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {c.why}
                      </Text>
                    </div>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={16} weight='bold' nowrap>
                      +{c.unlocks_alone}
                    </Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={14} color={TextColor.SECONDARY} nowrap>
                      {c.involved}
                    </Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12} color={EFFORT_COLOR[c.effort] ?? TextColor.SECONDARY} nowrap>
                      {c.effort || '—'}
                    </Text>
                  </TableRowItem>
                  <TableRowItem>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {c.build}
                    </Text>
                    <div className='mt-1'>
                      <Text size={10} color={TextColor.TERTIARY}>
                        {c.templates.slice(0, 6).join(' / ')}
                        {c.templates.length > 6 ? ` ほか${c.templates.length - 6}体` : ''}
                      </Text>
                    </div>
                  </TableRowItem>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </AreaContent>
      </Area>
    </>
  )
}

/** 「判定できる」の中身。何をどのテーブルからどう集計しているかを開いて見せる */
const JudgeDetail = ({
  persona,
  metrics
}: {
  persona: PersonaCatalogItem
  metrics: Map<string, PersonaMetricDef>
}) => (
  <div className='border-border mt-2 flex flex-col gap-3 rounded-lg border p-3'>
    <div>
      <Text size={10} color={TextColor.TERTIARY}>
        条件式（すべて満たす顧客が該当）
      </Text>
      {persona.condition_summary.map((line) => (
        <div key={line}>
          <Text size={12}>・{line}</Text>
        </div>
      ))}
    </div>

    {persona.draft_note && (
      <div>
        <Text size={10} color={TextColor.TERTIARY}>
          原文のうち条件式で表せていない部分
        </Text>
        <Text size={12} color={TextColor.CAUTION}>
          {persona.draft_note}
        </Text>
      </div>
    )}

    <div className='flex flex-col gap-2'>
      <Text size={10} color={TextColor.TERTIARY}>
        使っているメトリクスの集計内容
      </Text>
      {persona.metrics_used.map((key) => {
        const m = metrics.get(key)
        if (!m) {
          return (
            <div key={key}>
              <Text size={12} color={TextColor.CAUTION}>
                {key}（定義が見つかりません）
              </Text>
            </div>
          )
        }
        return (
          <div key={key} className='flex flex-col gap-1'>
            <Text size={12}>
              {key}
              {m.description ? ` — ${m.description}` : ''}
            </Text>
            <Text size={10} color={TextColor.SECONDARY}>
              参照テーブル: {m.source_tables.length > 0 ? m.source_tables.join(' / ') : '—'}
              {m.window_days ? ` ・ 集計期間: 直近${m.window_days}日` : ''}
              {m.default_when_missing !== null
                ? ` ・ 行が無い顧客は ${m.default_when_missing} として扱う`
                : ' ・ 行が無い顧客は判定不能'}
            </Text>
            <pre className='bg-secondary text-size-11 overflow-x-auto rounded p-2 whitespace-pre'>
              {m.sql_template}
            </pre>
          </div>
        )
      })}
    </div>
  </div>
)

/**
 * カタログの閾値を編集する（openspec: persona-definition-editing）。
 *
 * 編集フォームはサイト側と共用する。同じ条件式を別々の画面で編集できる状態を
 * 作ると、片方だけ直したときに表現できる式が食い違う。
 *
 * **採用済みサイトへは自動反映しない。** 版が上がり、サイト側に「新版あり」として
 * 見えるだけ。測定中のペルソナの定義が予告なく変わると効果測定が成立しないため、
 * その旨を画面にも出す。
 */
const CatalogEditor = ({
  persona,
  metrics,
  onSaved
}: {
  persona: PersonaCatalogItem
  metrics: Map<string, PersonaMetricDef>
  onSaved: () => void
}) => {
  const [open, setOpen] = useState(false)
  const initial = toEditableLeaves(persona.conditions)
  const [leaves, setLeaves] = useState<PersonaConditionLeaf[]>(initial ?? [])
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: detail } = usePersonaCatalogDetailApi(
    open ? { template_id: persona.template_id } : undefined
  )
  const { trigger: save } = useUpdatePersonaCatalogApi({
    onSuccess: () => {
      setSaving(false)
      setOpen(false)
      setNote('')
      onSaved()
    },
    onError: () => {
      setSaving(false)
      setError('保存できませんでした。条件式をご確認ください')
    }
  })

  // 条件式の形が画面で表せないものは編集させない（別の式に化けるより読み取り専用が良い）
  const brief: PersonaMetricBrief[] = [...metrics.values()].map((m) => ({
    metric_key: m.metric_key,
    description: m.description,
    value_type: m.value_type,
    window_days: m.window_days
  }))

  if (initial === null) {
    return (
      <Text size={10} color={TextColor.TERTIARY}>
        この条件式は入れ子や範囲指定を含むため、画面からは調整できません
      </Text>
    )
  }

  if (!open) {
    return (
      <Button
        color={ButtonColor.SECONDARY}
        onClick={() => {
          setLeaves(initial)
          setError(null)
          setOpen(true)
        }}
      >
        閾値を調整する
      </Button>
    )
  }

  return (
    <div className='border-border mt-2 flex flex-col gap-3 rounded-lg border p-3'>
      <Text size={10} color={TextColor.CAUTION}>
        ここはW2の共通資産です。
        {detail ? `現在 ${detail.adopted_site_count} サイトが採用しています。` : ''}
        保存しても採用済みサイトの判定条件は変わりません（各サイトに「新版あり」として表示され、取り込むかはサイトが選びます）。
      </Text>

      <ConditionLeafEditor leaves={leaves} metrics={brief} onChange={setLeaves} />

      <div className='flex flex-col gap-1'>
        <Text size={10} color={TextColor.SECONDARY}>
          変更の理由（履歴に残ります・任意）
        </Text>
        <input
          className='shadow-inset text-size-13 border-border-input bg-input rounded-lg border px-3 py-2'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='例：実データに合わせて30日を60日に緩和'
        />
      </div>

      {error && (
        <Text size={10} color={TextColor.CRITICAL}>
          {error}
        </Text>
      )}

      <div className='flex flex-wrap gap-2'>
        <Button
          disabled={saving || hasEmptyValue(leaves)}
          loading={saving}
          onClick={() => {
            setSaving(true)
            setError(null)
            save({
              template_id: persona.template_id,
              conditions: { all: leaves },
              ...(note.trim() ? { note: note.trim() } : {})
            })
          }}
        >
          保存
        </Button>
        <Button color={ButtonColor.SECONDARY} disabled={saving} onClick={() => setOpen(false)}>
          キャンセル
        </Button>
      </div>

      <div>
        <Text size={10} weight='bold'>
          変更履歴
        </Text>
        <div className='mt-1'>
          {detail ? (
            <DefinitionHistory items={detail.history} />
          ) : (
            <Text size={10} color={TextColor.SECONDARY}>
              読み込み中...
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}

const CatalogRow = ({
  persona,
  metrics,
  onSaved
}: {
  persona: PersonaCatalogItem
  metrics: Map<string, PersonaMetricDef>
  onSaved: () => void
}) => {
  const [open, setOpen] = useState(false)

  return (
    <TableRow>
      <TableRowItem align='top'>
        <div className='flex items-center gap-2'>
          <span
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[16px]'
            style={{ background: `${persona.avatar_color ?? '#4A4E69'}22` }}
          >
            {persona.avatar_emoji ?? '🧭'}
          </span>
          <div className='min-w-0'>
            <Text size={14}>{persona.name}</Text>
            <div>
              <Text size={10} color={TextColor.TERTIARY}>
                {persona.template_id}
                {persona.contribution ? ` ・ ${persona.contribution}` : ''}
              </Text>
            </div>
          </div>
        </div>
      </TableRowItem>
      <TableRowItem align='top'>
        <Text size={12} nowrap>
          {persona.difficulty}
        </Text>
      </TableRowItem>
      <TableRowItem align='top'>
        <Text size={12} color={TextColor.SECONDARY}>
          {persona.judge_text}
        </Text>
        {persona.judgeable && (
          <>
            <div className='mt-1'>
              <Button color={ButtonColor.SECONDARY} onClick={() => setOpen(!open)}>
                {open ? '集計内容を閉じる' : '集計内容を見る'}
              </Button>
            </div>
            {open && <JudgeDetail persona={persona} metrics={metrics} />}
            <div className='mt-1'>
              <CatalogEditor persona={persona} metrics={metrics} onSaved={onSaved} />
            </div>
          </>
        )}
      </TableRowItem>
      <TableRowItem align='top'>
        {persona.judgeable ? (
          <>
            <Text size={12} color={TextColor.SUCCESS} nowrap>
              判定できる
            </Text>
            <div>
              <Text size={10} color={TextColor.TERTIARY} nowrap>
                版 {persona.version}
              </Text>
            </div>
            {persona.draft_note && (
              <div className='mt-1'>
                <Text size={10} color={TextColor.CAUTION}>
                  原文の一部は未表現
                </Text>
              </div>
            )}
          </>
        ) : (
          <>
            <Text size={12} color={TextColor.SECONDARY} nowrap>
              判定条件が未作成
            </Text>
            {persona.blocked_labels.length > 0 && (
              <div className='mt-1'>
                <Text size={10} color={TextColor.TERTIARY}>
                  要: {persona.blocked_labels.join(' / ')}
                </Text>
              </div>
            )}
          </>
        )}
      </TableRowItem>
    </TableRow>
  )
}

/** カタログ取込（10.2）。既定は取り込まずに差分だけ見る */
const ImportPanel = () => {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<ImportPersonaCatalogResponse | null>(null)
  const { trigger, isMutating } = useImportPersonaCatalogApi()

  const run = async (dryRun: boolean) => {
    if (!file) return
    const r = await trigger({ file, dry_run: dryRun })
    setResult(r ?? null)
  }

  return (
    <Area>
      <Heading subText='130選のExcel（.xlsx）または書き出したJSONを取り込みます。中身が変わったものだけ版が上がります。'>
        カタログの取込
      </Heading>
      <AreaContent>
        <div className='flex flex-wrap items-center gap-3'>
          <input
            type='file'
            accept='.xlsx,.json'
            className='text-size-13'
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              setResult(null)
            }}
          />
          <Button
            color={ButtonColor.SECONDARY}
            disabled={!file}
            loading={isMutating}
            onClick={() => run(true)}
          >
            差分を見る
          </Button>
          <Button
            color={ButtonColor.PRIMARY}
            // 差分を見てからでないと適用させない。中身を見ずに版を上げる操作は事故になる
            disabled={!file || !result || !result.dry_run}
            loading={isMutating}
            onClick={() => run(false)}
          >
            取り込む
          </Button>
        </div>

        {result && (
          <div className='mt-4'>
            <Text size={14} weight='bold'>
              {result.dry_run ? '差分（まだ取り込んでいません）' : '取り込みました'}
            </Text>
            <div className='mt-1'>
              <Text size={12} color={TextColor.SECONDARY}>
                {result.file_name} ／ 全 {result.total} 体：新規 {result.created} ／ 版を上げる
                {result.updated} ／ 変更なし {result.unchanged} ／ 条件式あり {result.drafted}
              </Text>
            </div>
            {result.invalid.length > 0 && (
              <div className='mt-2'>
                <Text size={12} color={TextColor.CRITICAL}>
                  条件式が不正で入れられないもの {result.invalid.length} 件
                </Text>
                <ul className='mt-1'>
                  {result.invalid.slice(0, 10).map((m) => (
                    <li key={m}>
                      <Text size={10} color={TextColor.SECONDARY}>
                        ・{m}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.changes.length > 0 && (
              <div className='mt-2'>
                <Table>
                  <thead>
                    <TableRow head>
                      <TableHeadItem>
                        <Text size={12}>ID</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>名前</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>種別</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>変わる項目</Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {result.changes.map((c) => (
                      <TableRow key={c.template_id}>
                        <TableRowItem>
                          <Text size={12}>{c.template_id}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{c.name}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{c.kind === 'created' ? '新規' : '版を上げる'}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={10} color={TextColor.SECONDARY}>
                            {c.fields.join(' / ') || '—'}
                          </Text>
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {result.changes.length === 0 && (
              <div className='mt-1'>
                <Text size={12} color={TextColor.SECONDARY}>
                  変わるものはありません。
                </Text>
              </div>
            )}
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

const PersonaCatalogPage = () => {
  const [tab, setTab] = useState<'readiness' | 'catalog' | 'import'>('readiness')
  const [band, setBand] = useState<PersonaDifficultyBand | 'all'>('all')
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const { data, isLoading, mutate } = usePersonaCatalogApi(
    tab === 'catalog'
      ? {
          ...(band !== 'all' && { difficulty_band: band }),
          ...(keyword && { search: keyword })
        }
      : undefined
  )
  // メトリクス定義は一覧に1回だけ載っているので、行から引けるように詰め替える
  const metricsByKey = useMemo(
    () => new Map((data?.metrics ?? []).map((m) => [m.metric_key, m])),
    [data]
  )

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='ペルソナカタログ'
        description='ペルソナ130選はW2の共通資産です。ここは全体管理の閲覧画面で、サイトへは自動反映されません（サイトが採用したときにコピーされます）。'
      />
      <Content>
        <TabList border>
          <Tab selected={tab === 'readiness'} onClick={() => setTab('readiness')}>
            判定可能性
          </Tab>
          <Tab selected={tab === 'catalog'} onClick={() => setTab('catalog')}>
            カタログ一覧
          </Tab>
          <Tab selected={tab === 'import'} onClick={() => setTab('import')}>
            取込
          </Tab>
        </TabList>

        {tab === 'readiness' ? (
          <Readiness />
        ) : tab === 'import' ? (
          <ImportPanel />
        ) : (
          <Area>
            <AreaContent>
              <div className='mb-3 flex flex-wrap items-center gap-2'>
                {BANDS.map((b) => (
                  <Button
                    key={b.key}
                    color={band === b.key ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                    onClick={() => setBand(b.key)}
                  >
                    {b.label}
                  </Button>
                ))}
                <span className='flex-1' />
                <input
                  className={textInput}
                  placeholder='名前・ID・判定条件で絞り込み'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setKeyword(search.trim())
                  }}
                />
                <Button color={ButtonColor.SECONDARY} onClick={() => setKeyword(search.trim())}>
                  検索
                </Button>
              </div>

              {isLoading || !data ? (
                <Text color={TextColor.SECONDARY}>読み込み中...</Text>
              ) : (
                <>
                  <div className='mb-1'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {data.total} 体
                    </Text>
                  </div>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text size={12}>ペルソナ</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>難易度</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>判定条件（原文）</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>状態</Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {data.items.map((persona) => (
                        <CatalogRow
                          key={persona.template_id}
                          persona={persona}
                          metrics={metricsByKey}
                          onSaved={() => mutate()}
                        />
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </AreaContent>
          </Area>
        )}
      </Content>
    </Main>
  )
}

export default PersonaCatalogPage
